import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Create } from './create';
import {
  Entity,
  SendReportV1CreateContainerEntitiesDocument,
  SendReportV1CreateContainerVerifiedCredentialVerificationCaseSummariesByApplicantDocument,
  SendReportV1CreateContainerSendReportCaseV1Document,
  SendReportCaseV1CaseDetailsApplicationInput,
  SendReportV1CreateContainerSendReportCaseV1UpdateDocument,
  SendReportV1CreateContainerSendReportCaseV1LazyQueryDocument,
  SendReportV1CreateContainerSendReportCaseV1DeleteDocument,
  ApplicantCasesActiveCaseListContainerSendReportCasesV1ByApplicantQueryDocument,
  SendReportV1CreateContainerVerifiedCredentialVerificationCaseSummaryFieldsFragment
} from '../../../../../../../../generated';
import { ComponentQueryLoader } from '../../../../../../../shared/component-query-loader';
import { useEffect, useState } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';

interface CreateContainerProps {
  caseId: string;
  applicantUserId: string;
}
export const CreateContainer: React.FC<CreateContainerProps> = (props) => {
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const { data: entities, loading: entitiesLoading, error: entitiesError } = useQuery(SendReportV1CreateContainerEntitiesDocument);
  const navigate = useNavigate();
  const client = useApolloClient();
  const {
    data: sendReportCaseData,
    loading: sendReportCaseLoading,
    error: sendReportCaseError,
    refetch: getSendCaseV1
  } = useQuery(SendReportV1CreateContainerSendReportCaseV1Document, {
    variables: {
      sendReportCaseV1Id: props.caseId
    }
  });
  const {
    data: applicantCredentialCases,
    loading: applicantCredentialCasesLoading,
    error: applicantCredentialCasesError
  } = useQuery(SendReportV1CreateContainerVerifiedCredentialVerificationCaseSummariesByApplicantDocument, {
    variables: {
      applicantId: props.applicantUserId
    }
  });

  const [_paymentPollingData, { data: pollingData, startPolling, stopPolling }] = useLazyQuery(SendReportV1CreateContainerSendReportCaseV1LazyQueryDocument, {
    variables: { sendReportCaseV1Id: props.caseId }
  });

  useEffect(() => {
    const doRefetchCaseData = async () => {
      await getSendCaseV1({
        sendReportCaseV1Id: props.caseId
      });
      navigate(`../active/v1`);
    };

    const handlePollingData = async () => {
      if (pollingData?.sendReportCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.isSuccess === true) {
        stopPolling();
        setFormLoading(false);
        message.success('Case submitted successfully');
        await doRefetchCaseData();
      }
    };
    handlePollingData();
  }, [pollingData]);

  const updateSendCaseCache = async () => {
    client.cache.updateQuery(
      {
        query: ApplicantCasesActiveCaseListContainerSendReportCasesV1ByApplicantQueryDocument,
        variables: { applicantId: props.applicantUserId }
      },
      (existingData) => ({
        sendReportCasesV1ByApplicant: existingData?.sendReportCasesV1ByApplicant?.filter((item) => item?.id !== props.caseId) ?? []
      })
    );
  };

  const [deleteCase] = useMutation(SendReportV1CreateContainerSendReportCaseV1DeleteDocument, {
    onCompleted: (response) => {
      updateSendCaseCache()
        .then(() => {
          if (response?.sendReportCaseV1Delete?.status?.success === true) {
            navigate('/applicant/cases');
            message.success('Case withdrawn successfully');
          } else {
            message.error('Failed to withdraw case');
          }
        })
        .catch((error) => {
          message.error('Failed to update send case cache');
          console.error('Error updating send case cache', error);
        });
    }
  });

  const [processPayment] = useMutation(SendReportV1CreateContainerSendReportCaseV1UpdateDocument, {
    onCompleted: (response) => {
      const { status, sendReportCaseV1 } = response.sendReportCaseV1Update || {};
      if (status?.success === true && sendReportCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.status === 'PENDING') {
        try {
          startPolling(5000);
        } catch (error: any) {
          message.error(error.message);
          setFormLoading(false);
        }
      } else {
        message.error('Application submission failed: ' + status?.errorMessage);
        setFormLoading(false);
      }
    },
    onError: (error) => {
      message.error(`Error occurred while submitting application: ${error.message}`);
      setFormLoading(false);
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: SendReportV1CreateContainerSendReportCaseV1Document,
        variables: {
          sendReportCaseV1Id: props.caseId
        }
      }
    ]
  });

  const handleWithdrawRequest = async () => {
    setFormLoading(true);
    await deleteCase({
      variables: {
        input: {
          caseId: props.caseId
        }
      }
    });
    setFormLoading(false);
  };

  const handleSubmit = async (values: SendReportCaseV1CaseDetailsApplicationInput) => {
    setFormLoading(true);
    await processPayment({
      variables: {
        input: {
          id: props.caseId,
          caseDetails: {
            application: {
              ...values
            }
          },
          payment: {
            paymentAmount: sendReportCaseData?.sendReportCaseV1?.financeDetails?.serviceFee as number
          }
        }
      }
    });
  };

  return (
    <ComponentQueryLoader
      error={entitiesError || applicantCredentialCasesError || sendReportCaseError}
      loading={entitiesLoading || applicantCredentialCasesLoading || sendReportCaseLoading}
      hasData={entities && applicantCredentialCases && sendReportCaseData}
      hasDataComponent={
        <Create
          onSubmit={handleSubmit}
          onWithdrawRequest={handleWithdrawRequest}
          serviceFee={sendReportCaseData?.sendReportCaseV1?.financeDetails?.serviceFee as number}
          formLoading={formLoading}
          entities={entities?.entities as Entity[]}
          applicantCredentialCases={applicantCredentialCases?.verifiedCredentialVerificationCaseSummariesByApplicant as SendReportV1CreateContainerVerifiedCredentialVerificationCaseSummaryFieldsFragment[]}
        />
      }
    />
  );
};
